/**
 * useCommentVideoReply
 *
 * Surface half of the comments-sheet video reply — the web port of Flutter's
 * `_sendMediaMessage` (lib/presentation/bottom_sheet/comments_bottom_sheet.dart:891-930).
 *
 * It owns only the *surface orchestration*: capture the reply target, run the
 * auth / upload-in-progress preconditions, mint the comment id, hand the two
 * parity-bound payloads off to the domain builders in
 * `@/domain/utils/commentUtils`, open the add-content sheet in its comment-reply
 * variant, and sequence the post-confirm callbacks.
 *
 * Why it is NOT part of `useContentComments`: this hook opens a sheet.
 * `useContentComments` stays surface-agnostic (state + business flows only), so
 * the *business* half of the flow — `addComment` and `watchCommentMedia` — is
 * injected by the composing component (`ContentCommentsContent`) rather than
 * imported here. The composing component owns both hooks and wires one into the
 * other.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { AddContentSheet } from '@/presentation/feature/publish/components/sheets/addContent/AddContentSheet';
import { canOpenAddContent } from '@/presentation/feature/publish/utils/canOpenAddContent';
import { generateUuidV1 } from '@/domain/utils/uuidUtils';
import {
  buildContentReplyCommentInfo,
  buildVideoReplyPlaceholderMediaInfo,
} from '@/domain/utils/commentUtils';
import type { CommentEntity } from '@/domain/entities/comment/commentEntity';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { RootState } from '@/presentation/store';
import type { AddCommentOptions } from './useContentComments';

export interface UseCommentVideoReplyParams {
  /** The content being commented on — source of `mediaPath` and the placeholder `aspectRatio`. */
  media: UserMediaEntity;
  /** Live reply target (Flutter `_commentToReplyNotifier.value`, :892). Captured at tap time. */
  commentToReply: CommentEntity | null;
  /** Injected from `useContentComments` — writes the reply comment (Flutter `_addComment`, :926). */
  addComment: (text: string, options?: AddCommentOptions) => Promise<boolean>;
  /** Injected from `useContentComments` — opens the live media listener (Flutter `_subscribeToContentReply`, :927). */
  watchCommentMedia: (commentId: string) => void;
  /** Reads the composer draft at write time (Flutter `_textEditingController.text`, :468). */
  getDraftText: () => string;
  /** Clears the composer draft (Flutter `_textEditingController.clear()`, :516). */
  clearDraft: () => void;
  /** Screen key of the comments sheet, forwarded to the add-content sheet + the guard's info sheet. */
  screenKey?: string;
}

export interface UseCommentVideoReplyResult {
  /** Add-media tap handler. Flutter `_sendMediaMessage` (:891). */
  startVideoReply: () => void;
}

export function useCommentVideoReply(
  params: UseCommentVideoReplyParams,
): UseCommentVideoReplyResult {
  const {
    media,
    commentToReply,
    addComment,
    watchCommentMedia,
    getDraftText,
    clearDraft,
    screenKey,
  } = params;

  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  // Flutter's `AppAuthenticationService.getCurrentFirebaseUser()!` precondition
  // (:897) — the placeholder `MediaInfo.firebaseId` is the uploader's uid.
  const firebaseUserId = useSelector((state: RootState) => state.auth.firebaseUserId);

  // The `onUploaded` callback handed to the add-content sheet outlives many
  // renders of the comments sheet (the whole 5-step publish flow runs in
  // between), so reading the render-time closures would risk invoking a stale
  // `addComment`. Refs keep every injected callback at its latest value.
  const addCommentRef = useRef(addComment);
  const watchCommentMediaRef = useRef(watchCommentMedia);
  const getDraftTextRef = useRef(getDraftText);
  const clearDraftRef = useRef(clearDraft);

  useEffect(() => { addCommentRef.current = addComment; }, [addComment]);
  useEffect(() => { watchCommentMediaRef.current = watchCommentMedia; }, [watchCommentMedia]);
  useEffect(() => { getDraftTextRef.current = getDraftText; }, [getDraftText]);
  useEffect(() => { clearDraftRef.current = clearDraft; }, [clearDraft]);

  const startVideoReply = useCallback((): void => {
    // Flutter :892-893 — nothing happens without a selected reply target.
    // Captured here (not re-read later): the web comments sheet stays mounted
    // beneath the stacked add-content sheet, so the target is passed explicitly
    // instead of being stashed/restored by Flutter's didPushNext/didPopNext
    // (:164-184). A cancelled flow therefore leaves the reply bar untouched,
    // which is what Flutter's restore produces too.
    const selected = commentToReply;
    if (!selected) return;

    // Flutter force-unwraps the current Firebase user at :897.
    if (!firebaseUserId) return;

    // Flutter's upload-in-progress guard sits inside
    // `navigateToAddContentScreen` (navigation_service.dart:420-435), i.e.
    // after the target check and before the flow opens.
    if (!canOpenAddContent({ l10n, screenKey })) return;

    // Flutter :894 — minted BEFORE the upload so the transcode payload and the
    // comment doc agree on the id the server later looks the comment up by.
    const newCommentId = generateUuidV1();

    // Both payloads are parity-bound write shapes and are owned by the domain
    // builders (commentUtils.ts); this hook never constructs either literal.
    const mediaInfo = buildVideoReplyPlaceholderMediaInfo(media, firebaseUserId);
    const contentReplyCommentInfo = buildContentReplyCommentInfo(
      newCommentId,
      selected,
      media,
    );

    AddContentSheet.show({
      screenKey,
      width: responsive.addContentSheet.width,
      fixedHeight: responsive.addContentSheet.fixedHeight,
      commentReply: {
        contentReplyCommentInfo,
        onUploaded: (success: boolean): void => {
          // Flutter `if (uploaded)` (:925). `success` carries Flutter's
          // `bool uploaded` semantics — "the publish was confirmed and the
          // upload was kicked off", NOT "the storage upload succeeded"
          // (app_storage_service.dart:48-61 fires the upload un-awaited and
          // returns `true` at once). It is therefore `true` on every confirmed
          // publish; the guard mirrors Flutter's conditional.
          if (!success) return;

          void (async () => {
            // Flutter reads `_textEditingController.text` at :468 and clears it
            // at :516, both before awaiting the write — the composer draft is
            // consumed by the video reply.
            const text = getDraftTextRef.current();
            clearDraftRef.current();

            // Unconditional on purpose — NOT gated on the comments sheet still
            // being mounted. The server locates the comment by
            // `collectionGroup('comments').where('id','==',commentId)`
            // (content_utils.ts:199-207) and swallows a lookup failure
            // (:211-213), so skipping the write would orphan the uploaded video
            // with no comment. `addComment` already tolerates an unmounted host
            // (its `setComments` calls become no-ops).
            await addCommentRef.current(text, {
              commentId: newCommentId,
              mediaInfo,
              replyTo: selected,
            });

            // Flutter :927 — live-watch the new comment until the transcoder
            // stamps the real `mediaInfo` onto its doc.
            watchCommentMediaRef.current(newCommentId);
          })();
        },
      },
    });
  }, [commentToReply, media, firebaseUserId, screenKey, l10n, responsive]);

  return { startVideoReply };
}
